import { useCallback, useEffect } from 'react';
import { useI18nStore } from '@store/i18n';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from '../constants/languages';
import { getTranslation } from '../utils/getTranslation';
import type {
    SupportedLanguage,
    TranslationKey,
    TranslationParams,
} from '../types/i18nTypes';

export interface UseI18nReturn {
    /**
     * Current language
     */
    language: SupportedLanguage;
    /**
     * Change the language
     */
    changeLanguage: (language: SupportedLanguage) => void;
    /**
     * Get translation by key with variable interpolation
     * @param key - Translation key (e.g.: "header.menu.logout")
     * @param params - Object with variables for interpolation (e.g.: { name: "John" })
     * @param fallbackKey - Optional fallback key
     */
    t: (
        key: string,
        params?: TranslationParams,
        fallbackKey?: string,
    ) => string;
    /**
     * Get all translations for the current language
     */
    translations: TranslationKey;
    /**
     * Indicates if loading
     */
    isLoading: boolean;
    /**
     * Error loading translations
     */
    error: Error | null;
    /**
     * List of supported languages
     */
    supportedLanguages: SupportedLanguage[];
}

/**
 * Hook for internationalization
 *
 * @example
 * ```tsx
 * const { t, language, changeLanguage } = useI18n();
 *
 * // Basic usage
 * <h1>{t('common.welcome')}</h1>
 *
 * // With interpolation
 * <p>{t('common.greeting', { name: 'John' })}</p>
 *
 * // Change language
 * <button onClick={() => changeLanguage(SUPPORTED_LANGUAGES.EN_GB)}>
 *   English
 * </button>
 * ```
 */
export function useI18n(): UseI18nReturn {
    const {
        language,
        translations,
        isLoading,
        error,
        setLanguage,
        loadTranslations,
    } = useI18nStore();

    // Load translations on mount
    useEffect((): void => {
        loadTranslations();
    }, [loadTranslations]);

    // Load saved preference on mount
    useEffect((): void => {
        const savedLanguage = localStorage.getItem(
            'dynee-language',
        ) as SupportedLanguage | null;
        if (
            savedLanguage &&
            Object.values(SUPPORTED_LANGUAGES).includes(savedLanguage)
        ) {
            setLanguage(savedLanguage);
        }
    }, [setLanguage]);

    const changeLanguage = useCallback(
        (newLanguage: SupportedLanguage): void => {
            setLanguage(newLanguage);
        },
        [setLanguage],
    );

    const t = useCallback(
        (
            key: string,
            params?: TranslationParams,
            fallbackKey?: string,
        ): string => {
            if (!translations[language]) {
                return fallbackKey ?? key;
            }

            const fallbackLang = fallbackKey
                ? translations[fallbackKey as SupportedLanguage]
                    ? (fallbackKey as SupportedLanguage)
                    : DEFAULT_LANGUAGE
                : DEFAULT_LANGUAGE;

            return getTranslation(
                key,
                language,
                translations,
                fallbackLang,
                params,
            );
        },
        [language, translations],
    );

    return {
        language,
        changeLanguage,
        t,
        translations: translations[language] ?? {},
        isLoading,
        error,
        supportedLanguages: Object.values(SUPPORTED_LANGUAGES),
    };
}
